import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, MessageSquare, PackageCheck, PackagePlus, RotateCcw, Star, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, INVOICE_STATUS, ORDER_STATUS, SHIPMENT_STATUS } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const SHIP_METHODS = [
    ["own_vehicle", "Firmamız Araçları"],
    ["cargo", "Kargo"],
    ["freight", "Nakliye"],
    ["other", "Diğer"],
];

const FLOW = ["pending", "confirmed", "preparing", "shipped", "delivered"];
const FLOW_TR = { pending: "Alındı", confirmed: "Onaylandı", preparing: "Hazırlanıyor", shipped: "Kargoda", delivered: "Teslim" };

export default function OrderDetail() {
    const { orderId } = useParams();
    const { user } = useAuth();
    const [order, setOrder] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelNote, setCancelNote] = useState("");
    const [shipOpen, setShipOpen] = useState(false);
    const [shipMethod, setShipMethod] = useState("");
    const [shipFields, setShipFields] = useState({});
    const [rateOpen, setRateOpen] = useState(false);
    const [rateKind, setRateKind] = useState("reward");
    const [rateScore, setRateScore] = useState(5);
    const [rateDesc, setRateDesc] = useState("");
    const [retOpen, setRetOpen] = useState(false);
    const [retReason, setRetReason] = useState("");
    const [retNote, setRetNote] = useState("");
    const [rejOpen, setRejOpen] = useState(false);
    const [rejNote, setRejNote] = useState("");
    const [delOpen, setDelOpen] = useState(false);
    const [delQtys, setDelQtys] = useState({});
    const [delNote, setDelNote] = useState("");

    const RETURN_REASONS = ["Ürün hasarlı geldi", "Yanlış ürün gönderildi", "Eksik ürün", "Son kullanma tarihi uygun değil", "Diğer"];

    const requestReturn = async () => {
        if (!retReason) return toast.error("İade sebebi seçin");
        setBusy(true);
        try {
            const r = await api.post(`/orders/${orderId}/return-request`, { reason: retReason, note: retNote || undefined });
            setOrder(r.data);
            setRetOpen(false);
            toast.success("İade talebi tedarikçiye iletildi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "İade talebi gönderilemedi");
        } finally {
            setBusy(false);
        }
    };

    const reviewReturn = async (approve) => {
        setBusy(true);
        try {
            const r = await api.post(`/orders/${orderId}/return-review`, { approve, note: approve ? undefined : rejNote });
            setOrder(r.data);
            setRejOpen(false);
            toast.success(approve ? "İade onaylandı — stok iade edildi" : "İade reddedildi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(false);
        }
    };

    const CANCEL_REASONS = ["Stok yetersizliği", "Fiyat anlaşmazlığı", "Teslimat süresi uymuyor", "Yanlış sipariş oluşturuldu", "Müşteri talebinden vazgeçti", "Diğer"];
    const isSupplier = user?.role === "supplier";

    const load = () => api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
    useEffect(() => { load().catch(() => {}); }, [orderId]);

    const setStatus = async (status, extra = {}) => {
        setBusy(true);
        try {
            const r = await api.post(`/orders/${orderId}/status`, { status, ...extra });
            setOrder(r.data);
            setShipOpen(false);
            toast.success("Sipariş güncellendi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(false);
        }
    };

    const submitDelivery = async () => {
        const items = Object.entries(delQtys).map(([pid, q]) => ({ product_id: pid, qty: parseFloat(q) || 0 })).filter((i) => i.qty > 0);
        if (!items.length) return toast.error("En az bir kalem için miktar girin");
        setBusy(true);
        try {
            const r = await api.post(`/orders/${orderId}/deliveries`, { items, note: delNote || undefined });
            setOrder(r.data);
            setDelOpen(false);
            setDelQtys({});
            setDelNote("");
            toast.success("Kısmi teslimat kaydedildi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    const receiveDelivery = async (deliveryId) => {
        setBusy(true);
        try {
            const r = await api.post(`/deliveries/${deliveryId}/receive`);
            toast.success(r.data.order_completed ? "Tüm teslimatlar tamamlandı — sipariş teslim edildi" : "Teslimat onaylandı");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Onaylanamadı");
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        if (!cancelReason) return toast.error("İptal sebebi seçin");
        setBusy(true);
        try {
            await api.post(`/orders/${orderId}/cancel`, { reason: cancelReason, note: cancelNote || undefined });
            toast.success("Sipariş iptal edildi");
            setConfirmCancel(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İptal edilemedi");
        } finally {
            setBusy(false);
        }
    };

    const submitRating = async () => {
        if (!rateDesc.trim()) return toast.error("Açıklama zorunludur");
        setBusy(true);
        try {
            await api.post("/complaints", {
                target_type: "supplier",
                target_id: order.supplier_id,
                kind: rateKind,
                rating: rateScore,
                description: rateDesc.trim(),
                order_id: order.order_id,
            });
            toast.success(rateKind === "reward" ? "Ödül kaydı iletildi" : "Şikayetiniz iletildi");
            setRateOpen(false);
            setRateDesc("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gönderilemedi");
        } finally {
            setBusy(false);
        }
    };

    const startOrderMessage = async () => {
        try {
            const r = await api.post("/messages/conversations", {
                supplier_id: order.supplier_id,
                order_id: order.order_id,
                subject: `Sipariş: ${order.order_no}`,
                content: `${order.order_no} numaralı sipariş hakkında yazıyorum.`,
            });
            window.location.href = `/panel/mesajlar?conv=${r.data.conversation_id}`;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Yazışma başlatılamadı");
        }
    };

    if (!order) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    const flowIdx = order.status === "cancelled" ? -1 : FLOW.indexOf(order.status);
    const remainingTotal = order.items.reduce((a, i) => a + (i.qty - (i.delivered_qty || 0)), 0);

    return (
        <div className="space-y-6" data-testid="order-detail-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Link to="/panel/siparisler" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="back-to-orders">
                    <ArrowLeft className="h-4 w-4" /> Siparişlere dön
                </Link>
                <div className="flex gap-2" data-testid="order-actions">
                    {isSupplier && order.status === "pending" && (
                        <Button disabled={busy} onClick={() => setStatus("confirmed")} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="order-confirm-button"><Check className="mr-2 h-4 w-4" /> Siparişi Onayla</Button>
                    )}
                    {isSupplier && order.status === "confirmed" && (
                        <Button disabled={busy} onClick={() => setStatus("preparing")} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="order-prepare-button"><PackageCheck className="mr-2 h-4 w-4" /> Hazırlamaya Başla</Button>
                    )}
                    {isSupplier && order.status === "preparing" && (
                        <Button disabled={busy} onClick={() => setShipOpen(true)} className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="order-ship-button"><Truck className="mr-2 h-4 w-4" /> Gönder</Button>
                    )}
                    {isSupplier && ["confirmed", "preparing", "shipped"].includes(order.status) && remainingTotal > 0 && (
                        <Button disabled={busy} onClick={() => { setDelQtys({}); setDelNote(""); setDelOpen(true); }} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="order-partial-delivery-button"><PackagePlus className="mr-2 h-4 w-4" /> Kısmi Teslimat</Button>
                    )}
                    {!isSupplier && order.status === "shipped" && (
                        <Button disabled={busy} onClick={() => setStatus("delivered")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="order-deliver-button"><Check className="mr-2 h-4 w-4" /> Teslim Aldım</Button>
                    )}
                    {!isSupplier && (
                        <Button variant="outline" onClick={startOrderMessage} data-testid="order-message-button"><MessageSquare className="mr-2 h-4 w-4" /> Sipariş Mesajı</Button>
                    )}
                    {!isSupplier && order.status === "delivered" && !order.return_status && (
                        <Button variant="outline" onClick={() => setRetOpen(true)} className="text-orange-700" data-testid="order-return-button"><RotateCcw className="mr-2 h-4 w-4" /> İade Talebi</Button>
                    )}
                    {!isSupplier && order.status === "delivered" && (
                        <Button variant="outline" onClick={() => setRateOpen(true)} className="text-amber-700" data-testid="order-rate-button"><Star className="mr-2 h-4 w-4" /> Değerlendir</Button>
                    )}
                    {isSupplier && order.return_status === "requested" && (
                        <>
                            <Button disabled={busy} onClick={() => reviewReturn(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="return-approve-button"><Check className="mr-2 h-4 w-4" /> İadeyi Onayla</Button>
                            <Button disabled={busy} variant="outline" className="text-destructive" onClick={() => setRejOpen(true)} data-testid="return-reject-button"><XCircle className="mr-2 h-4 w-4" /> İadeyi Reddet</Button>
                        </>
                    )}
                    {["pending", "confirmed"].includes(order.status) && (
                        <Button disabled={busy} variant="outline" className="text-destructive" onClick={() => setConfirmCancel(true)} data-testid="order-cancel-button"><XCircle className="mr-2 h-4 w-4" /> İptal Et</Button>
                    )}
                </div>
            </div>

            <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
                <DialogContent data-testid="order-cancel-confirm">
                    <DialogHeader>
                        <DialogTitle className="font-display">Sipariş İptali — {order?.order_no}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Ayrılan stok serbest bırakılacak ve karşı tarafa bildirim gönderilecek.</p>
                        <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason-select">
                            <option value="">İptal sebebi seçin *</option>
                            {CANCEL_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <Textarea rows={2} value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder={cancelReason === "Diğer" ? "Açıklama *" : "Ek not (opsiyonel)"} data-testid="cancel-note-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmCancel(false)}>Vazgeç</Button>
                        <Button onClick={cancel} disabled={busy || !cancelReason} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="cancel-confirm-button">
                            {busy ? "İptal ediliyor..." : "Evet, İptal Et"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={shipOpen} onOpenChange={setShipOpen}>
                <DialogContent data-testid="ship-method-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Gönderim Şekli — {order?.order_no}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={shipMethod} onChange={(e) => { setShipMethod(e.target.value); setShipFields({}); }} data-testid="ship-method-select">
                            <option value="">Gönderim şekli seçin *</option>
                            {SHIP_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        {shipMethod === "cargo" && (
                            <>
                                <Input placeholder="Kargo firması * (örn: Aras Kargo)" value={shipFields.cargo_firm || ""} onChange={(e) => setShipFields({ ...shipFields, cargo_firm: e.target.value })} data-testid="ship-cargo-firm" />
                                <Input placeholder="Kargo takip numarası *" value={shipFields.tracking_no || ""} onChange={(e) => setShipFields({ ...shipFields, tracking_no: e.target.value })} data-testid="ship-tracking-no" />
                            </>
                        )}
                        {shipMethod === "freight" && (
                            <>
                                <Input placeholder="Nakliyeci adı soyadı *" value={shipFields.freight_name || ""} onChange={(e) => setShipFields({ ...shipFields, freight_name: e.target.value })} data-testid="ship-freight-name" />
                                <Input placeholder="Plaka *" value={shipFields.freight_plate || ""} onChange={(e) => setShipFields({ ...shipFields, freight_plate: e.target.value })} data-testid="ship-freight-plate" />
                                <Input placeholder="İletişim numarası *" value={shipFields.freight_phone || ""} onChange={(e) => setShipFields({ ...shipFields, freight_phone: e.target.value })} data-testid="ship-freight-phone" />
                            </>
                        )}
                        {shipMethod === "other" && (
                            <Textarea rows={2} placeholder="Açıklama *" value={shipFields.ship_note || ""} onChange={(e) => setShipFields({ ...shipFields, ship_note: e.target.value })} data-testid="ship-other-note" />
                        )}
                        {shipMethod === "own_vehicle" && <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">Firmamız araçları ile gönderim — ek bilgi gerekmez.</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShipOpen(false)}>Vazgeç</Button>
                        <Button
                            onClick={() => setStatus("shipped", { ship_method: shipMethod, ...shipFields })}
                            disabled={busy || !shipMethod}
                            className="bg-purple-700 text-white hover:bg-purple-800"
                            data-testid="ship-confirm-button"
                        >
                            {busy ? "Gönderiliyor..." : "Gönderimi Onayla"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={delOpen} onOpenChange={setDelOpen}>
                <DialogContent data-testid="partial-delivery-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Kısmi Teslimat — {order?.order_no}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Bu partide teslim edilen miktarları girin. Alıcı her partiyi ayrıca onaylar; tüm kalemler tamamlanınca sipariş otomatik kapanır.</p>
                        {order.items.map((it) => {
                            const remaining = it.qty - (it.delivered_qty || 0);
                            return (
                                <div key={it.product_id} className="flex items-center gap-3" data-testid={`del-row-${it.product_id}`}>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold">{it.product_name} <QualityBadge quality={it.quality} tid={`del-${it.product_id}`} /></p>
                                        <p className="text-xs text-muted-foreground">Kalan: {fmtQty4(remaining, it.unit)} {it.unit}</p>
                                    </div>
                                    <Input
                                        type="number" min="0" max={remaining} step="any" placeholder="0"
                                        value={delQtys[it.product_id] ?? ""}
                                        onChange={(e) => setDelQtys({ ...delQtys, [it.product_id]: e.target.value })}
                                        className="w-28" disabled={remaining <= 0}
                                        data-testid={`del-qty-${it.product_id}`}
                                    />
                                </div>
                            );
                        })}
                        <Textarea rows={2} value={delNote} onChange={(e) => setDelNote(e.target.value)} placeholder="Not (opsiyonel — örn: araç plakası, parti no)" data-testid="del-note-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDelOpen(false)}>Vazgeç</Button>
                        <Button onClick={submitDelivery} disabled={busy} className="bg-teal-700 text-white hover:bg-teal-800" data-testid="del-submit-button">
                            {busy ? "Kaydediliyor..." : "Teslimatı Kaydet"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={retOpen} onOpenChange={setRetOpen}>
                <DialogContent data-testid="order-return-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">İade Talebi — {order?.order_no}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Talebiniz tedarikçiye iletilir; onaylanırsa ürünler stoğa iade edilir ve ücret iadesi cari hesabınıza yansıtılır.</p>
                        <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={retReason} onChange={(e) => setRetReason(e.target.value)} data-testid="return-reason-select">
                            <option value="">İade sebebi seçin *</option>
                            {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <Textarea rows={2} value={retNote} onChange={(e) => setRetNote(e.target.value)} placeholder={retReason === "Diğer" ? "Açıklama *" : "Ek not (opsiyonel)"} data-testid="return-note-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRetOpen(false)}>Vazgeç</Button>
                        <Button onClick={requestReturn} disabled={busy || !retReason} className="bg-orange-600 text-white hover:bg-orange-700" data-testid="return-submit-button">
                            {busy ? "Gönderiliyor..." : "Talebi Gönder"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={rejOpen} onOpenChange={setRejOpen}>
                <DialogContent data-testid="return-reject-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">İade Reddi — {order?.order_no}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Textarea rows={3} value={rejNote} onChange={(e) => setRejNote(e.target.value)} placeholder="Ret sebebi * (alıcıya bildirilecek)" data-testid="return-reject-note" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejOpen(false)}>Vazgeç</Button>
                        <Button onClick={() => reviewReturn(false)} disabled={busy || !rejNote.trim()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="return-reject-confirm">
                            {busy ? "Gönderiliyor..." : "Reddet"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={rateOpen} onOpenChange={setRateOpen}>
                <DialogContent data-testid="order-rate-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Siparişi Değerlendir — {order?.order_no}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex gap-2">
                            {[["reward", "Ödül"], ["complaint", "Şikayet"]].map(([k, l]) => (
                                <button key={k} onClick={() => setRateKind(k)} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${rateKind === k ? "border-terra-600 bg-terra-600 text-white" : "text-muted-foreground hover:border-terra-400"}`} data-testid={`rate-kind-${k}`}>{l}</button>
                            ))}
                        </div>
                        <div className="flex items-center gap-1" data-testid="rate-stars">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} onClick={() => setRateScore(n)} data-testid={`rate-star-${n}`}>
                                    <Star className={`h-7 w-7 transition-colors ${n <= rateScore ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
                                </button>
                            ))}
                            <span className="ml-2 text-sm font-semibold">{rateScore}/5</span>
                        </div>
                        <Textarea rows={3} value={rateDesc} onChange={(e) => setRateDesc(e.target.value)} placeholder="Açıklama *" data-testid="rate-description-input" />
                        <p className="text-xs text-muted-foreground">Değerlendirme {order?.supplier_name} için kaydedilir; şikayetler platform yönetimine bildirilir.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRateOpen(false)}>Vazgeç</Button>
                        <Button onClick={submitRating} disabled={busy || !rateDesc.trim()} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="rate-submit-button">
                            {busy ? "Gönderiliyor..." : "Gönder"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="font-display text-2xl font-bold" data-testid="order-number">{order.order_no}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {isSupplier ? order.buyer_company_name : order.supplier_name} • {fmtDate(order.created_at)} • {order.source === "rfq" ? "RFQ kaynaklı" : "Sepet siparişi"}
                        </p>
                    </div>
                    <Badge variant="outline" className={`text-sm ${ORDER_STATUS[order.status]?.cls}`} data-testid="order-status-badge">{ORDER_STATUS[order.status]?.label}</Badge>
                    {order.return_status === "requested" && <Badge variant="outline" className="border-orange-200 bg-orange-100 text-sm text-orange-800" data-testid="return-status-badge">İade Talebi Bekliyor</Badge>}
                    {order.return_status === "rejected" && <Badge variant="outline" className="border-red-200 bg-red-100 text-sm text-red-700" data-testid="return-status-badge">İade Reddedildi</Badge>}
                </div>

                {!["cancelled", "returned"].includes(order.status) && (
                    <div className="mt-6 flex items-center" data-testid="order-timeline">
                        {FLOW.map((s, i) => (
                            <div key={s} className="flex flex-1 items-center last:flex-none">
                                <div className="flex flex-col items-center">
                                    <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold ${i <= flowIdx ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300 text-stone-400"}`}>
                                        {i + 1}
                                    </div>
                                    <span className={`mt-1.5 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide ${i <= flowIdx ? "text-emerald-700" : "text-stone-400"}`}>{FLOW_TR[s]}</span>
                                </div>
                                {i < FLOW.length - 1 && <div className={`mx-2 mb-5 h-0.5 flex-1 ${i < flowIdx ? "bg-emerald-600" : "bg-stone-200"}`} />}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm lg:col-span-7">
                    <div className="border-b bg-muted/50 px-5 py-3"><h2 className="font-display font-bold">Sipariş Kalemleri</h2></div>
                    {order.items.map((it, i) => (
                        <div key={i} className="flex items-center gap-4 border-b px-5 py-4 last:border-0" data-testid={`order-item-${i}`}>
                            <img src={it.image} alt="" className="h-12 w-12 rounded-xl object-cover" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold">{it.product_name} <QualityBadge quality={it.quality} tid={`order-${i}`} /></p>
                                <p className="text-xs text-muted-foreground">{fmtQty4(it.qty, it.unit)} {it.unit} × {fmtTL(it.unit_price)}{it.delivery_days ? ` • Teslim ~${it.delivery_days} gün` : ""}</p>
                                {(it.delivered_qty || 0) > 0 && (
                                    <div className="mt-1.5 flex items-center gap-2">
                                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-stone-200">
                                            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, Math.round((it.delivered_qty / it.qty) * 100))}%` }} />
                                        </div>
                                        <span className="text-[10px] font-semibold text-emerald-700" data-testid={`item-delivered-${i}`}>{fmtQty4(it.delivered_qty, it.unit)}/{fmtQty4(it.qty, it.unit)} {it.unit} teslim edildi</span>
                                    </div>
                                )}
                            </div>
                            <p className="font-display font-extrabold text-terra-600">{fmtTL(it.line_total)}</p>
                        </div>
                    ))}
                    <div className="space-y-1.5 px-5 py-4 text-sm">
                        <div className="flex justify-between text-muted-foreground"><span>Ara Toplam</span><span>{fmtTL(order.subtotal)}</span></div>
                        <div className="flex justify-between text-muted-foreground"><span>KDV (%10)</span><span>{fmtTL(order.vat)}</span></div>
                        <div className="flex justify-between border-t pt-2"><span className="font-display font-bold">Toplam</span><span className="font-display text-lg font-extrabold text-terra-600" data-testid="order-total">{fmtTL(order.total)}</span></div>
                    </div>
                </div>

                <div className="space-y-6 lg:col-span-5">
                    {order.shipment && (
                        <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="order-shipment-card">
                            <h3 className="font-display font-bold">Sevkiyat</h3>
                            <div className="mt-3 space-y-2 text-sm">
                                {order.shipment.ship_method && (
                                    <div className="flex justify-between"><span className="text-muted-foreground">Gönderim Şekli</span><span className="font-medium">{Object.fromEntries(SHIP_METHODS)[order.shipment.ship_method]}</span></div>
                                )}
                                {order.shipment.ship_method === "cargo" && (
                                    <>
                                        <div className="flex justify-between"><span className="text-muted-foreground">Kargo Firması</span><span className="font-medium">{order.shipment.cargo_firm}</span></div>
                                        <div className="flex justify-between"><span className="text-muted-foreground">Takip No</span><span className="font-mono text-xs font-semibold">{order.shipment.tracking_no}</span></div>
                                    </>
                                )}
                                {order.shipment.ship_method === "freight" && (
                                    <>
                                        <div className="flex justify-between"><span className="text-muted-foreground">Nakliyeci</span><span className="font-medium">{order.shipment.freight_name}</span></div>
                                        <div className="flex justify-between"><span className="text-muted-foreground">Plaka</span><span className="font-mono text-xs font-semibold">{order.shipment.freight_plate}</span></div>
                                        <div className="flex justify-between"><span className="text-muted-foreground">İletişim</span><span className="font-medium">{order.shipment.freight_phone}</span></div>
                                    </>
                                )}
                                {order.shipment.ship_method === "other" && order.shipment.ship_note && (
                                    <div className="flex justify-between"><span className="text-muted-foreground">Açıklama</span><span className="font-medium">{order.shipment.ship_note}</span></div>
                                )}
                                {!order.shipment.ship_method && (
                                    <>
                                        <div className="flex justify-between"><span className="text-muted-foreground">Kargo</span><span className="font-medium">{order.shipment.carrier}</span></div>
                                        <div className="flex justify-between"><span className="text-muted-foreground">Takip No</span><span className="font-mono text-xs font-semibold">{order.shipment.tracking_no}</span></div>
                                    </>
                                )}
                                <div className="flex justify-between"><span className="text-muted-foreground">Durum</span><Badge variant="outline" className={`text-[10px] ${SHIPMENT_STATUS[order.shipment.status]?.cls}`}>{SHIPMENT_STATUS[order.shipment.status]?.label}</Badge></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Tahmini Teslim</span><span className="font-medium">{fmtDate(order.shipment.estimated_delivery)}</span></div>
                                {order.shipment.receiver && <div className="flex justify-between"><span className="text-muted-foreground">Teslim Alan</span><span className="font-medium">{order.shipment.receiver}</span></div>}
                            </div>
                        </div>
                    )}
                    {order.invoice && (
                        <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="order-invoice-card">
                            <h3 className="font-display font-bold">Fatura</h3>
                            <div className="mt-3 space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Fatura No</span><span className="font-mono text-xs font-semibold">{order.invoice.invoice_no}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Tutar</span><span className="font-display font-extrabold text-terra-600">{fmtTL(order.invoice.total)}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Durum</span><Badge variant="outline" className={`text-[10px] ${INVOICE_STATUS[order.invoice.status]?.cls}`}>{INVOICE_STATUS[order.invoice.status]?.label}</Badge></div>
                            </div>
                        </div>
                    )}
                    {order.deliveries?.length > 0 && (
                        <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="order-deliveries-card">
                            <h3 className="font-display font-bold">Kısmi Teslimatlar ({order.deliveries.length})</h3>
                            <div className="mt-3 space-y-3">
                                {order.deliveries.map((d) => (
                                    <div key={d.delivery_id} className="rounded-xl border p-3" data-testid={`delivery-${d.seq}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-sm font-semibold">Teslimat #{d.seq}</p>
                                            {d.status === "received"
                                                ? <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800" data-testid={`delivery-status-${d.seq}`}>Teslim Alındı</Badge>
                                                : <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800" data-testid={`delivery-status-${d.seq}`}>Teslim Onayı Bekliyor</Badge>}
                                        </div>
                                        <p className="mt-1 text-xs text-muted-foreground">{d.items.map((i) => `${i.product_name} ${fmtQty4(i.qty, i.unit)} ${i.unit}`).join(", ")}</p>
                                        {d.note && <p className="mt-0.5 text-xs text-muted-foreground">Not: {d.note}</p>}
                                        <div className="mt-2 flex items-center justify-between gap-2">
                                            <p className="text-[10px] text-muted-foreground">{fmtDate(d.created_at)}{d.received_at ? ` • alındı: ${fmtDate(d.received_at)} (${d.received_by})` : ""}</p>
                                            {!isSupplier && d.status === "pending_receipt" && (
                                                <Button size="sm" disabled={busy} onClick={() => receiveDelivery(d.delivery_id)} className="h-7 bg-emerald-700 text-xs text-white hover:bg-emerald-800" data-testid={`delivery-receive-${d.seq}`}>Teslim Aldım</Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="order-events">
                        <h3 className="font-display font-bold">Sipariş Geçmişi</h3>
                        <div className="mt-3 space-y-3">
                            {[...order.events].reverse().map((e, i) => (
                                <div key={i} className="flex gap-3 text-sm">
                                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${i === 0 ? "bg-terra-500" : "bg-stone-300"}`} />
                                    <div>
                                        <p className="font-medium">{e.note}</p>
                                        <p className="text-xs text-muted-foreground">{fmtDate(e.at)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
